import React, {createRef, ReactElement, RefObject} from 'react';
import MComponent from '../Toolkit/Components/Component';
import {Column} from 'primereact/column';
import MTable, {MTableFilters} from './Toolkit/Table';
import NSwagAdapter from '../Toolkit/Api/NSwagAdapter';
import MNSwagClientConfiguration from '../Toolkit/Api/NSwagClientConfiguration';
import TrafficManagementAccessTokenProvider from '../Api/TrafficManagementAccessTokenProvider';
import {IMsalContext} from '@azure/msal-react';
import MButton from '../Toolkit/Components/Controls/Button';
import {MNewIconName} from '../Toolkit/Icons';
import MSpacer from '../Toolkit/Components/Controls/Spacer';
import MToolbar from '../Toolkit/Components/Controls/Toolbar';
import {Drawing, DrawingMultiReply, DrawingReply} from '../Api/ControllerTypes/DrawingTypes';
import {CreateDrawingRequest, DrawingClient, UpdateDrawingRequest} from '../Api/DrawingClient';
import DrawingDialog from '../Dialogs/DrawingDialog';
import MessageDialog from '../Dialogs/MessageDialog';
import EnumContext from '../Context/enum-context';
import {DataTable} from 'primereact/datatable';
import OkCancelDialog from '../Dialogs/OkCancelDialog';

type DrawingRow = {
    data: Drawing;
}

type Props = {
    parentId: string;
    msalContext: IMsalContext;
    baseDrawingRef?: string;
    onSendEmail?: () => void;
    subDrawingName?: string;
}

/** */
type State = {
    rowData: DrawingRow[];
    filters: MTableFilters;
    selectedRow?: DrawingRow;
}

class PermitControl extends MComponent<Props, State> {
    static contextType = EnumContext;
    context!: React.ContextType<typeof EnumContext>;

    private readonly m_drawingDialogRef: RefObject<DrawingDialog>;
    private readonly m_drawingClient: NSwagAdapter<DrawingClient>;
    private readonly m_messageDialogRef: RefObject<MessageDialog>;
    private readonly m_tableRef: RefObject<DataTable>;
    private readonly m_okCancelRef: RefObject<OkCancelDialog>;

    private pounds = Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
    });

    public constructor(props: Props) {
        super(props);

        this.m_drawingDialogRef = createRef<DrawingDialog>();
        this.m_messageDialogRef = createRef<MessageDialog>();
        this.m_tableRef = createRef<DataTable>();
        this.m_drawingClient = new NSwagAdapter<DrawingClient>(new DrawingClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new TrafficManagementAccessTokenProvider(this.props.msalContext))));
        this.m_okCancelRef = createRef<OkCancelDialog>();

        this.bindMethods();
        this.initializeState();
    }

    private bindMethods(): void {
        this.table_onSelectionChange = this.table_onSelectionChange.bind(this);
        this.loadPageData = this.loadPageData.bind(this);
        this.onNew_Click = this.onNew_Click.bind(this);
        this.onUpdate_Click = this.onUpdate_Click.bind(this);
        this.onDelete_Click = this.onDelete_Click.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onEmail_Click = this.onEmail_Click.bind(this);
        this.onDrawingUpdate = this.onDrawingUpdate.bind(this);
        this.drawnByTemplate = this.drawnByTemplate.bind(this);
        this.completedTemplate = this.completedTemplate.bind(this);
        this.paidTemplate = this.paidTemplate.bind(this);
        this.paidTotalTemplate = this.paidTotalTemplate.bind(this);
        this.chargeTemplate = this.chargeTemplate.bind(this);
        this.chargeTotalTemplate = this.chargeTotalTemplate.bind(this);
        this.netProfitTemplate = this.netProfitTemplate.bind(this);
        this.isPlanner = this.isPlanner.bind(this);
    }

    async componentDidMount() {
        await this.loadPageData();
    }

    private table_onSelectionChange(selection: DrawingRow | DrawingRow[] | undefined): void {

        this.safeSetState((draft: State) =>
            draft.selectedRow = selection as DrawingRow | undefined);

    }

    private initializeState(): void {
        this.state = {
            filters: {},
            rowData: []
        };
    }

    private async onNew_Click(): Promise<void> {
        const newDrawing : Drawing = {
            id: 0,
            parentId: this.props.parentId,
            drawingNum: '',
            drawnBy: -1,
            numOfDrawings: 0,
            paidPerPlan: 0,
            chargePerPlan: 0,
            createdBy: '',
            createdOn: new Date(),
            requestDate: undefined
        }
        this.m_drawingDialogRef.current!.show(true, newDrawing, this.onDrawingUpdate);
    }

    private onUpdate_Click(): void {
        this.m_drawingDialogRef.current!.show(false, this.state.selectedRow!.data, this.onDrawingUpdate);
    }

    private async onDelete_Click(): Promise<void> {
        this.m_okCancelRef.current!.show('Delete Drawing', `This will delete file ${this.state.selectedRow?.data.drawingNum}.`)
    }

    private async onDelete(): Promise<void> {
        const deleteResult = await this.m_drawingClient.call(
            (client) => client.delete(this.state.selectedRow!.data.id));
        deleteResult.throwIfFailed();

        this.loadPageData();
    }

    private async onEmail_Click(): Promise<void> {
        this.props.onSendEmail!();
    }

    private async onDrawingUpdate(newData: boolean, data: Drawing) {
        if(newData) {
            for(let drawingCount = 0; drawingCount < data.numOfDrawings; drawingCount++)
            {
                const drawingReply = await this.m_drawingClient.callWithReply(
                    DrawingReply, (client => client.create(new CreateDrawingRequest({
                        parentId: data.parentId,
                        drawnBy: data.drawnBy,
                        numOfDrawings: 1,
                        paidPerPlan: data.paidPerPlan,
                        chargePerPlan: data.chargePerPlan
                    })))
                );
                drawingReply.throwIfFailed();
            }
        } else {
            const drawingReply = await this.m_drawingClient.callWithReply(
                DrawingReply, (client => client.update(new UpdateDrawingRequest({
                    id: data.id,
                    drawnBy: data.drawnBy,
                    numOfDrawings: 1,
                    paidPerPlan: data.paidPerPlan,
                    chargePerPlan: data.chargePerPlan
                })))
            );
            drawingReply.throwIfFailed();
        }

        this.safeSetState((draft: State) =>
            draft.selectedRow = undefined);
        this.m_tableRef.current!.clearState();
        await this.loadPageData();
    }

    private isPlanner(): boolean {
        const allowedRoles: string[] = ['Booking', 'Supervisor'];
        const roles = this.props.msalContext.accounts[0].idTokenClaims!.roles;
        const access = allowedRoles.some(a => roles?.includes(a));

        return !access;
    }

    private async loadPageData(): Promise<void> {

        //Read drawing data.
        const drawingResult = await this.m_drawingClient.callWithReply(
            DrawingMultiReply, (client) => client.getAll2(this.props.parentId));
        drawingResult.throwIfFailed();
        const rowData = drawingResult.reply!.drawings.map(n => ({data: n}));

        this.safeSetState(draft => {
            draft.rowData = rowData;
        });
    }

    private drawnByTemplate(rowData: DrawingRow): string | null {
        if(rowData.data.drawnBy > 0) {
            const index = this.context.enums?.drawnByValues.findIndex(value => value.id === rowData.data.drawnBy);

            if(index === undefined || index === -1) {
                return null;
            }

            return this.context.enums?.drawnByValues[index!].name!;
        }

        return rowData.data.createdBy;
    }

    private completedTemplate(rowData: DrawingRow): string | null {
        if(rowData.data.requestDate !== undefined && rowData.data.requestDate !== null){
            return rowData.data.requestDate.toLocaleDateString();
        }

        return '';
    }

    private paidTemplate(rowData: DrawingRow): string | null {
        return this.pounds.format(rowData.data.paidPerPlan);
    }

    private paidTotalTemplate(rowData: DrawingRow): string | null {
        return this.pounds.format(rowData.data.numOfDrawings * rowData.data.paidPerPlan);
    }

    private chargeTemplate(rowData: DrawingRow): string | null {
        return this.pounds.format(rowData.data.chargePerPlan);
    }

    private chargeTotalTemplate(rowData: DrawingRow): string | null {
        return this.pounds.format(rowData.data.numOfDrawings * rowData.data.chargePerPlan);
    }

    private netProfitTemplate(rowData: DrawingRow): string | null {
        return this.pounds.format((rowData.data.numOfDrawings * rowData.data.chargePerPlan) -
            (rowData.data.numOfDrawings * rowData.data.paidPerPlan));
    }

    public render(): ReactElement {
        return (
            <React.Fragment>
                <MToolbar>
                    <MButton label="Add Drawing"
                             color="success"
                             icon={MNewIconName}
                             disabled={this.isPlanner}
                             onClick={this.onNew_Click}/>
                    <MSpacer/>
                    <MButton label="Edit"
                             icon={'pi pi-pencil'}
                             disabled={this.state.selectedRow === undefined || this.isPlanner}
                             onClick={this.onUpdate_Click}/>
                    <MSpacer/>
                    <OkCancelDialog ref={this.m_okCancelRef}
                                    okClicked={this.onDelete}/>
                    <MButton label="Delete"
                             icon={'pi pi-trash'}
                             color={'danger'}
                             disabled={this.state.selectedRow === undefined || this.isPlanner}
                             onClick={this.onDelete_Click}/>
                    {this.props.onSendEmail !== undefined &&
                        <React.Fragment>
                            <MSpacer/>
                            <MButton label="Email to Sub"
                                     color='warning'
                                     icon={'pi pi-send'}
                                     disabled={this.isPlanner}
                                     onClick={this.onEmail_Click}/>
                        </React.Fragment>}
                    <MSpacer/>
                </MToolbar>

                <MessageDialog ref={this.m_messageDialogRef}/>
                <DrawingDialog ref={this.m_drawingDialogRef}
                               drawnByValues={this.context.enums?.drawnByValues ?? []}
                />

                <div className="col-12 md:col-12" style={{marginTop: "2rem"}}>
                    <p style={{fontWeight: 'bold', fontSize: 'large', paddingBottom: '1rem'}}>
                        Drawing Ref: {this.props.baseDrawingRef}
                    </p>
                    {(this.props.subDrawingName !== undefined && this.props.subDrawingName !== null)
                        && <p style={{fontWeight: 'bold', fontSize: 'large', paddingBottom: '1rem', color: '#FBC02D'}}>
                        Drawing Request has been sent to sub contractor: {this.props.subDrawingName}
                    </p>}
                </div>

                <MTable
                    rowKey="data.id"
                    rowData={this.state.rowData}
                    filters={this.state.filters}
                    onSelectionChange={this.table_onSelectionChange}
                    sortField={'data.modifiedOn'}
                    sortOrder={1}
                    innerRef={this.m_tableRef}
                >

                    <Column field="data.drawingNum"
                            header="Drawing Number"
                            filter={false}
                            sortable={true}
                            style={{width: '20%'}}/>
                    <Column field="data.drawnBy"
                            header="Drawn By"
                            body={this.drawnByTemplate}
                            filter={false}
                            sortable={true}
                            style={{width: '20%'}}/>
                    <Column field="data.numOfDrawings"
                            header="Drawing Count"
                            filter={false}
                            sortable={true}
                            style={{width: '10%'}}/>
                    <Column header="Paid Per Plan"
                            body={this.paidTemplate}
                            filter={false}
                            sortable={true}
                            style={{width: '10%'}}/>
                    <Column header="Total Paid"
                            body={this.paidTotalTemplate}
                            filter={false}
                            sortable={true}
                            style={{width: '10%'}}/>
                    <Column header="Charge Per Plan"
                            body={this.chargeTemplate}
                            filter={false}
                            sortable={true}
                            style={{width: '10%'}}/>
                    <Column header="Total Charged"
                            body={this.chargeTotalTemplate}
                            filter={false}
                            sortable={true}
                            style={{width: '10%'}}/>
                    <Column header="Net Profit"
                            body={this.netProfitTemplate}
                            filter={false}
                            sortable={true}
                            style={{width: '10%'}}/>
                </MTable>
            </React.Fragment>
        );
    }
}

export default PermitControl;
